import { Component, Type, inject, ViewChild, OnInit } from '@angular/core';
import { FileUploadComponent } from '../../../shared/components/file-upload/file-upload.component';
import { MatIcon } from '@angular/material/icon';
import { Router } from '@angular/router';
import { TranslocoDirective, TranslocoModule } from '@jsverse/transloco';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';

import { ResetTextButtonComponent } from '../../../shared/components/reset-text-button/reset-text-button.component';
import { ColorPickerComponent } from '../../../shared/components/color-picker/color-picker.component';
import { PrimaryButtonComponent } from '../../../shared/components/primary-button/primary-button.component';
import { VariableTopicareaContainerComponent } from '../../../domains/organization/organization/components/variable-topicarea-container/variable-topicarea-container.component';
import { LearningPlanService } from '../../../domains/academy/learning-plan/services/learning-plan.service';
import { CustomPortalError } from '../../../core/errors/models/CustomPortalError';
import { SnackbarService } from '../../../core/services/snackbar.service';
import { CourseVariablesService } from '../../../domains/academy/course/services/course-variables.service';
import { SavingBannerComponent } from '../../../shared/components/saving-banner/saving-banner.component';
import { CourseVariablesDataService } from '../../../domains/organization/organization/services/course-variables-data.service';
import { CourseVariable } from '../../../domains/organization/organization/models/CourseVariables';
import { AnalyticsService } from '../../../core/services/analytics/analytics.service';

interface Tab {
  key: string;
  icon: string;
  component: Type<unknown>;
  wrapperClass?: string;
  isLocked?: boolean;
}

const colorKey = 'TenantColor';
const logoKey = 'TenantLogo';
const logoMaxWidth = 70;
const logoMaxHeight = 30;

@Component({
  selector: 'app-course-settings',
  imports: [
    MatIcon,
    CommonModule,
    FormsModule,
    MatExpansionModule,
    MatTabsModule,
    MatTooltipModule,
    TranslocoModule,
    TranslocoDirective,
    FileUploadComponent,
    ResetTextButtonComponent,
    ColorPickerComponent,
    PrimaryButtonComponent,
    SavingBannerComponent,
  ],
  templateUrl: './course-settings.component.html',
  styleUrl: './course-settings.component.css',
})
export class CourseSettingsComponent implements OnInit {
  @ViewChild('logoUpload', { static: false }) logoUpload:
    | FileUploadComponent
    | undefined;
  private readonly analyticsService = inject(AnalyticsService);
  private readonly router = inject(Router);
  private readonly learningPlanService = inject(LearningPlanService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly courseVariablesDataService = inject(
    CourseVariablesDataService,
  );
  public readonly courseVariablesService = inject(CourseVariablesService);

  tabs: Tab[] = [
    {
      key: 'CyberSecurity',
      icon: 'security',
      component: VariableTopicareaContainerComponent,
      wrapperClass: 'w-full',
    },
    {
      key: 'Compliance',
      icon: 'gavel',
      component: VariableTopicareaContainerComponent,
      wrapperClass: 'w-full',
    },
    {
      key: 'DataProtection',
      icon: 'check_circle',
      component: VariableTopicareaContainerComponent,
      wrapperClass: 'w-full',
    },
    {
      key: 'OccupationalSafety',
      icon: 'construction',
      component: VariableTopicareaContainerComponent,
      wrapperClass: 'w-full',
    },
  ];

  public readonly defaultAccentColor = '#ff9100';
  public readonly defaultLogoUrl = '/assets/logo.svg';

  _accentColor = this.defaultAccentColor;
  previousAccentColor = this.defaultAccentColor;
  _companyLogoUrl = this.defaultLogoUrl;
  previousCompanyLogoUrl = this.defaultLogoUrl;
  logoImgStyle: { width: string; height: string } = {
    width: 'auto',
    height: 'auto',
  };
  defaultTabIndex = 0;
  selectedTabIndex = 0;

  get accentColor() {
    return this._accentColor;
  }

  set accentColor(color: string) {
    this._accentColor = color.toLowerCase();
  }

  get companyLogoUrl() {
    return this._companyLogoUrl;
  }

  set companyLogoUrl(logo: string) {
    if (!logo.startsWith('data:image/') && logo !== this.defaultLogoUrl) {
      throw Error('invalid logo url');
    }

    this._companyLogoUrl = logo;
  }

  get pendingChangesCount(): number {
    return this.getPendingChanges().length;
  }

  get hasPendingChanges(): boolean {
    return this.pendingChangesCount > 0;
  }

  private getPendingChanges(): CourseVariable[] {
    const changes: CourseVariable[] = [];
    if (this.accentColor !== this.previousAccentColor) {
      changes.push({
        key: colorKey,
        value:
          this.accentColor !== this.defaultAccentColor
            ? this.accentColor
            : null,
      });
    }
    if (this.companyLogoUrl !== this.previousCompanyLogoUrl) {
      changes.push({
        key: logoKey,
        value:
          this.companyLogoUrl !== this.defaultLogoUrl
            ? this.companyLogoUrl
            : null,
      });
    }

    return changes;
  }

  onResetToDefault(): void {
    this.accentColor = this.defaultAccentColor;
    this.resetLogoToDefault();
  }

  resetLogoToDefault(): void {
    this.companyLogoUrl = this.defaultLogoUrl;
    this.computeLogoImgStyle(this.defaultLogoUrl);
    this.logoUpload?.reset();
  }

  onLogoSelected(dataUrl: string): void {
    if (dataUrl !== this.companyLogoUrl) {
      this.companyLogoUrl = dataUrl;
      this.computeLogoImgStyle(dataUrl);
    }
  }

  private computeLogoImgStyle(dataUrl: string): void {
    if (dataUrl === this.defaultLogoUrl) {
      this.logoImgStyle = { width: '26.5px', height: '15.5px' };
      return;
    }

    const img = new Image();
    img.onload = () => {
      const constrainByWidth =
        img.naturalWidth / logoMaxWidth >= img.naturalHeight / logoMaxHeight;
      this.logoImgStyle = constrainByWidth
        ? { width: `${logoMaxWidth}px`, height: 'auto' }
        : { width: 'auto', height: `${logoMaxHeight}px` };
    };
    img.src = dataUrl;
  }

  onAccentColorChange(color: string): void {
    if (color.toLowerCase() !== this.accentColor) {
      this.accentColor = color;
    }
  }

  saveGeneralSettings(): void {
    const changes = this.getPendingChanges();

    if (changes.length === 0) {
      return;
    }

    this.courseVariablesDataService.setCourseVariables(changes).subscribe({
      next: () => {
        this.snackbarService.success(
          'portal.courseSettings.generalSettings.successfulSavedVariablesSnackbar',
        );
        this.analyticsService.track('course_branding_customized');

        this.previousAccentColor = this.accentColor;
        this.previousCompanyLogoUrl = this.companyLogoUrl;
      },
      error: (error: CustomPortalError) => {
        this.snackbarService.error(error.errorType);
      },
    });
  }

  ngOnInit(): void {
    this.initializeTopicAreas();
    this.initializeCourseVariables();
    this.computeLogoImgStyle(this.companyLogoUrl);
  }

  onTabChange(tabIndex: number): void {
    if (this.selectedTabIndex === tabIndex) {
      return;
    }
    if (this.tabs[tabIndex]) {
      void this.router.navigate([], {
        queryParams: { tab: this.tabs[tabIndex].key },
        queryParamsHandling: 'merge',
      });
    }
  }

  private initializeCourseVariables(): void {
    this.courseVariablesDataService.getCourseVariables().subscribe({
      next: (variables) => {
        const colorIndex = variables.findIndex(
          (variable) => variable.key === colorKey,
        );
        if (colorIndex >= 0) {
          this.accentColor = variables[colorIndex].value ?? '';
          this.previousAccentColor = this.accentColor;
          variables.splice(colorIndex, 1);
        }
        const logoIndex = variables.findIndex(
          (variable) => variable.key === logoKey,
        );
        if (logoIndex >= 0) {
          this.companyLogoUrl = variables[logoIndex].value ?? '';
          this.previousCompanyLogoUrl = this.companyLogoUrl;
          this.computeLogoImgStyle(this.companyLogoUrl);
          variables.splice(logoIndex, 1);
        }

        this.courseVariablesService.initializeStore(variables);
      },
      error: (error: CustomPortalError) => {
        this.snackbarService.error(error.errorType);
      },
    });
  }

  private initializeTopicAreas(): void {
    this.learningPlanService.getTopicAreas().subscribe({
      next: (response) => {
        const availableTopicAreas = response.topicAreas.map(
          (item) => item.name,
        );
        this.tabs = this.tabs.map((tabobject) => ({
          ...tabobject,
          isLocked: !availableTopicAreas.includes(tabobject.key),
        }));

        this.selectedTabIndex = this.tabs.findIndex((item) => !item.isLocked);
        this.onTabChange(this.selectedTabIndex);
      },
      error: (error: CustomPortalError) => {
        this.snackbarService.error(error.errorType);
      },
    });
  }
}
